<template>
  <div class="w-full p-4">
    <!-- The band stays on screen, as on My Expenses. -mt-4 cancels the
         page's own p-4: without it the band rests 16px below where it pins
         and the whole page slides that far before it catches. -->
    <div class="sticky top-14 z-30 bg-gray-200 -mx-4 -mt-4 px-4 pt-3 pb-4">
      <div class="flex flex-row justify-between items-center pb-2 border-b border-b-gray-400 mb-2">
        <h2 class="text-lg font-semibold">Blockers</h2>
        <button
          class="px-3 py-1.5 text-sm rounded-md border border-dark-primary text-dark-primary
                 hover:bg-dark-primary hover:text-white"
          @click="load"
        >
          Refresh
        </button>
      </div>

      <p class="text-sm text-gray-500">
        What the team is waiting on. {{ open.length }} open,
        {{ resolved.length }} resolved.
      </p>
    </div>

    <p v-if="loading" class="mt-6 text-gray-500">Loading…</p>
    <p v-else-if="error" class="mt-6 text-red-600">{{ error }}</p>

    <template v-else>
      <h3 class="font-semibold text-dark-primary border-b border-gray-300 pb-1 mt-6 mb-2">Open</h3>
      <p v-if="!open.length" class="text-gray-500 text-sm">
        Nothing is blocked right now.
      </p>
      <div v-for="item in open" :key="item.id" class="border border-red-200 bg-red-50 rounded-md p-3 mb-2">
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <p class="text-sm whitespace-pre-line">{{ item.description }}</p>
            <p class="text-xs text-gray-600 mt-1">
              {{ item.target_label }}
              <span v-if="item.waiting_on"> · waiting on {{ item.waiting_on }}</span>
              <span v-if="item.raised_by_display"> · raised by {{ item.raised_by_display }}</span>
              · {{ item.raised_at.slice(0, 10) }}
            </p>
          </div>
          <div class="flex items-center gap-2 shrink-0">
            <input
              v-model="resolutions[item.id]"
              class="h-9 px-2 border border-gray-400 rounded-md text-xs w-56"
              placeholder="How was it resolved?"
            />
            <button
              class="px-3 py-1.5 rounded-md text-xs border border-gray-400 bg-white
                     hover:bg-gray-100 disabled:opacity-50"
              :disabled="!(resolutions[item.id] || '').trim() || saving === item.id"
              @click="resolve(item)"
            >Resolve</button>
          </div>
        </div>
      </div>

      <h3 class="font-semibold text-dark-primary border-b border-gray-300 pb-1 mt-8 mb-2">Resolved</h3>
      <p v-if="!resolved.length" class="text-gray-500 text-sm">Nothing resolved yet.</p>
      <div v-for="item in resolved" :key="item.id" class="border border-gray-300 rounded-md bg-white p-3 mb-1.5">
        <p class="text-sm">{{ item.description }}</p>
        <p class="text-xs text-gray-500 mt-1">
          {{ item.target_label }} · {{ item.resolution_note }}
          <span v-if="item.resolved_at"> · {{ item.resolved_at.slice(0, 10) }}</span>
        </p>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'

import { getBlockers, updateBlocker } from '@/services/BackOffice'

const store = useStore()
const token = () => store.getters['auth/showToken']

const blockers = ref([])
const resolutions = ref({})
const loading = ref(true)
const saving = ref(null)
const error = ref('')

const open = computed(() => blockers.value.filter(b => b.status === 'open'))
const resolved = computed(() => blockers.value.filter(b => b.status === 'resolved'))

async function load() {
  loading.value = true
  error.value = ''
  try {
    const data = await getBlockers(token(), { get_all: 'true' })
    blockers.value = Array.isArray(data) ? data : (data.results || [])
  } catch (e) {
    error.value = describe(e)
  } finally {
    loading.value = false
  }
}

async function resolve(item) {
  saving.value = item.id
  error.value = ''
  try {
    // The server rejects a resolve with no note, so send both together.
    await updateBlocker(token(), item.id, {
      status: 'resolved',
      resolution_note: (resolutions.value[item.id] || '').trim(),
    })
    delete resolutions.value[item.id]
    await load()
  } catch (e) {
    error.value = describe(e)
  } finally {
    saving.value = null
  }
}

function describe(e) {
  if (e?.response?.status) {
    const detail = e.response.data ? JSON.stringify(e.response.data) : ''
    return `${e.response.status} ${e.response.statusText || ''} ${detail}`.trim()
  }
  return String(e)
}

onMounted(load)
</script>
